/**
 * Estilos para a página de edição de recursos.
 *
 * Este módulo contém os componentes estilizados utilizados para renderizar
 * a interface de edição de recursos, incluindo o layout principal, formulário,
 * botão de voltar e contêiner de alertas.
 *
 * @module EditResources.Styles
 */

import styled from '@emotion/styled';

/**
 * Componente estilizado para o contêiner principal da página.
 *
 * Define o layout flexível em coluna para o contêiner principal com:
 * - Display flex com direção de coluna
 * - Margem superior e esquerda
 * - Fundo branco
 * - Sombra sutil
 *
 * @type {import('@emotion/styled').StyledComponent}
 */
export const Main = styled.main`
  display: flex;
  flex-direction: column;
  margin-top: 1em;
  margin-left: 2.2em;
  background-color: #ffffff;
  box-shadow: -2px 1px 7px -6px rgba(0, 0, 0, 0.6);
`;

/**
 * Componente estilizado para o contêiner do formulário.
 *
 * Define o estilo para o formulário e seus elementos internos:
 * - Layout flexível em coluna
 * - Padding interno
 * - Estilos para o formulário e seus campos (select, input)
 * - Estilos para estados de erro
 * - Posicionamento do botão de salvar
 * - Estado de carregamento
 *
 * @type {import('@emotion/styled').StyledComponent}
 */
export const FormStyle = styled.div`
  display: flex;
  flex-direction: column;
  padding-inline: 5%;
  padding-block: 2%;

  form {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding-top: 3%;

    select {
      height: 2.2rem;
      width: 100%;
      border: 0.5px solid rgba(207, 207, 207, 0.69);
      border-radius: 2px;
      font-size: 0.8rem;
      padding-left: 7px;
      color: rgb(0, 0, 0);
      outline: none;
      align-items: center;
      box-shadow: 1px 1px 2px -1px rgba(207, 207, 207, 0.64);
    }

    input {
      height: 2.2rem;
      width: 98%;
      border: 0.5px solid rgba(207, 207, 207, 0.69);
      border-radius: 2px;
      font-size: 0.8rem;
      color: rgb(0, 0, 0);
      padding-left: 15px;
      align-items: center;
      box-shadow: 1px 1px 2px -1px rgba(207, 207, 207, 0.64);
      outline: none;
    }

    .error {
      border: 1px solid red !important;
      outline: red;
    }

    .button-save {
      display: flex;
      justify-content: flex-end;
      margin-top: 25px;
    }
  }

  .loading {
    display: flex;
    justify-content: center;
    padding: 10%;
  }
`;

/**
 * Componente estilizado para o botão de voltar.
 *
 * Define o estilo para o contêiner do botão de voltar com:
 * - Layout flexível com alinhamento à esquerda
 * - Espaçamento entre elementos
 * - Alinhamento vertical centralizado
 * - Margem inferior
 * - Estilos para o botão e texto
 *
 * @type {import('@emotion/styled').StyledComponent}
 */
export const Back = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 10px;
  justify-items: center;
  align-items: center;
  margin-bottom: 0.5em;

  button {
    border: none;
    background-color: transparent;
    cursor: pointer;
  }

  span {
    font-size: 1.6rem;
  }
`;

/**
 * Componente estilizado para o contêiner de alertas.
 *
 * Define o estilo para alertas flutuantes que aparecem na parte superior da tela:
 * - Posição fixa no topo e centralizada horizontalmente
 * - Z-index elevado para aparecer sobre outros elementos
 * - Largura responsiva com máximo definido
 * - Layout flexível em coluna para múltiplos alertas
 * - Espaçamento entre alertas
 *
 * @type {import('@emotion/styled').StyledComponent}
 */
export const AlertContainer = styled.div`
  position: fixed;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  width: 90%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
